import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { withTooltip } from '@crossnokaye/ux-common';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crossnokaye/ux-common/ckds';

import {
  DropdownButton,
  DropdownButtonProps,
} from '@/ux-common/components/dropdown-button/dropdown-button';
import { Inline } from '@/ux-common/components/inline/inline';
import { cn } from '@/ux-common/lib/utils';

const DropdownButtonWithTooltip = withTooltip(DropdownButton);

export interface DropdownCellOption {
  value: string;
  label: string;
  /** Optional icon node rendered before the label on the trigger and menu item. */
  icon?: ReactNode;
}

/** `DropdownButtonProps` without the keys this component owns. */
type BaseButtonProps = Omit<DropdownButtonProps, 'onSelect' | 'value'>;

export interface DropdownCellProps<T extends DropdownCellOption = DropdownCellOption>
  extends BaseButtonProps {
  /** Currently selected value (controlled). */
  value: string | null | undefined;
  /** Selectable options. */
  options: T[];
  /** Called with the full option when the user selects one. */
  onSelect: (option: T) => void;
  /**
   * When provided and a value is selected, renders a "Clear selection" item at
   * the top of the menu that invokes this handler.
   */
  onClear?: () => void;
  /**
   * Disable the trigger so the menu can't be opened. Use when the control is
   * unavailable outright (e.g. a precondition isn't met); surface the reason
   * via `tooltipContent`, shown on hover of the disabled trigger.
   */
  disabled?: boolean;
  /**
   * Grey out the menu items while still allowing the menu to open. Use when the
   * user may view the available options but isn't permitted to choose one (e.g.
   * permission-gated rows). The trigger stays interactive; pair with
   * `tooltipContent` to explain why selection is blocked.
   */
  disableItems?: boolean;
  /** Heading rendered above the options. */
  subtitle?: string;
  /** Placeholder shown when nothing is selected. */
  placeholder?: string;
  /** `data-state-name` applied to the trigger. */
  stateName?: string;
  /** Tooltip shown on trigger hover — typically explains a `disabled`/`disableItems` state. */
  tooltipContent?: string;
  /** Classes applied to the trigger button (e.g. variant, full-bleed styling). */
  className?: string;
  /** Classes applied to the menu content (e.g. width, max-height, z-index). */
  contentClassName?: string;
  /** Message shown when there are no selectable options. */
  emptyMessage?: string;
}

/**
 * Dropdown cell renderer.
 *
 * Controlled, presentation-only dropdown built on the Radix `DropdownMenu` +
 * `DropdownButton` primitives. Renders the selected option's label (with an
 * optional injected icon and tooltip) on the trigger and a menu of the
 * remaining options — the currently selected option is hidden from the list.
 * Supports an optional clear action and an empty state.
 *
 * Styling (button variant, full-bleed cell appearance, menu width/z-index) is
 * supplied entirely through `className`/`contentClassName`, so the component
 * stays decoupled from any feature.
 */
export const DropdownCell = <T extends DropdownCellOption = DropdownCellOption>({
  disabled,
  disableItems,
  options,
  subtitle,
  onSelect,
  onClear,
  placeholder,
  stateName,
  tooltipContent,
  value,
  className,
  contentClassName,
  emptyMessage = 'No options available',
  ...props
}: DropdownCellProps<T>) => {
  const currentOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  const visibleOptions = useMemo(
    () => options.filter((option) => option.value !== value),
    [options, value]
  );

  const showClear = onClear !== undefined && value !== null && value !== undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-state-name={stateName}
        asChild
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          className="h-full w-full"
          data-component="dropdown-cell"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DropdownButtonWithTooltip
            className={cn(
              'btn-sm w-full [&>span:first-child]:min-w-0 [&>span:first-child]:overflow-hidden',
              className
            )}
            delayDuration={0}
            tooltipContent={tooltipContent}
            {...props}
          >
            <Inline className="flex flex-row gap-x-2 min-w-0 overflow-hidden">
              {currentOption?.icon}
              <span className="truncate">
                {currentOption?.label ?? value?.toString() ?? placeholder ?? 'Unknown'}
              </span>
            </Inline>
          </DropdownButtonWithTooltip>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn('min-w-72', contentClassName)}
        data-component="dropdown-cell-content"
        collisionPadding={8}
        onClick={(e) => e.stopPropagation()}
        withPortal
      >
        {subtitle && (
          <>
            <DropdownMenuLabel>{subtitle}</DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        {showClear && (
          <>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onClear?.();
              }}
            >
              Clear selection
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {visibleOptions.length === 0 ? (
          <DropdownMenuItem disabled>{emptyMessage}</DropdownMenuItem>
        ) : (
          visibleOptions.map((option) => (
            <DropdownMenuItem
              data-component="dropdown-cell-option"
              data-value={option.value}
              disabled={disableItems || disabled}
              key={option.value}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(option);
              }}
            >
              {option.icon}
              <span className="text-ckds-neutral-900">{option.label}</span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

DropdownCell.displayName = 'DataTable.DropdownCell';
